import React, { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, RefreshCcw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  superAdminGetProjects,
  superAdminGetTransactions,
  superAdminUpdateTransaction,
} from '../../api/api';
import StyledSelect from '../../components/StyledSelect';
import { ActionIconButton, Chip, SuperAdminPanel } from '../components/SuperAdminUi';

const typeOptions = [
  { value: '', label: 'All types' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'payment', label: 'Payment' },
];

const currencyOptions = ['PKR', 'USD', 'EUR', 'SAR'];

export default function SuperAdminTransactions() {
  const [searchParams] = useSearchParams();
  const queryTransactionId = Number(searchParams.get('transactionId') || 0);
  const queryType = String(searchParams.get('type') || '').trim().toLowerCase();

  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({
    type: queryType === 'receipt' || queryType === 'payment' ? queryType : '',
    project_id: '',
    search: '',
  });

  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({
    description: '',
    type: 'receipt',
    amount: '',
    date: '',
    project_id: '',
    currency: 'PKR',
    payment_method: '',
    person: '',
    reference_no: '',
    transaction_time: '',
  });

  const projectOptions = useMemo(() => {
    return [{ value: '', label: 'All projects' }, ...projects.map((project) => ({
      value: String(project.id),
      label: `${project.name || 'Unnamed'}${project.organization_name ? ` • ${project.organization_name}` : ''}`,
    }))];
  }, [projects]);

  const load = async () => {
    setLoading(true);
    setError('');

    try {
      const [transactionRes, projectRes] = await Promise.all([
        superAdminGetTransactions({
          page: 1,
          limit: 1000,
          ...(filters.type ? { type: filters.type } : {}),
          ...(filters.project_id ? { project_id: Number(filters.project_id) } : {}),
        }),
        superAdminGetProjects({ page: 1, limit: 1000 }),
      ]);

      const txRows = transactionRes?.data?.data?.transactions || [];
      const searchedRows = txRows.filter((row) => {
        const term = String(filters.search || '').trim().toLowerCase();
        if (!term) return true;
        const haystack = [
          row.description,
          row.user_name,
          row.user_email,
          row.project_name,
          row.organization_name,
          row.type,
          row.date,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });

      setRows(searchedRows);
      setProjects(projectRes?.data?.data?.projects || []);
    } catch (requestError) {
      setRows([]);
      setProjects([]);
      setError(requestError?.response?.data?.message || 'Failed to load transactions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filters.type, filters.project_id, filters.search]);

  useEffect(() => {
    if (!queryTransactionId || rows.length === 0) return;
    const target = rows.find((row) => Number(row.id) === queryTransactionId);
    if (target) {
      openEdit(target);
    }
  }, [queryTransactionId, rows]);

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      description: row?.description || '',
      type: row?.type || 'receipt',
      amount: row?.amount ?? '',
      date: row?.date || '',
      project_id: String(row?.project_id || ''),
      currency: row?.currency || 'PKR',
      payment_method: row?.payment_method || '',
      person: row?.person || '',
      reference_no: row?.reference_no || '',
      transaction_time: row?.transaction_time || '',
    });
  };

  const saveTransaction = async () => {
    if (!editRow?.id) return;
    if (!form.project_id) {
      setError('A project is required to update this transaction.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await superAdminUpdateTransaction(editRow.id, {
        description: form.description,
        type: form.type,
        amount: Number(form.amount),
        date: form.date,
        project_id: Number(form.project_id),
        currency: form.currency,
        payment_method: form.payment_method || null,
        person: form.person || null,
        reference_no: form.reference_no || null,
        transaction_time: form.transaction_time || null,
      });
      setSuccess('Transaction updated successfully.');
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to update transaction.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="superadmin-title text-3xl font-bold">Transaction Governance</h1>
          <p className="text-slate-500">Review and manage transactions across all organizations and projects.</p>
        </div>
        <button type="button" className="ems-btn-secondary text-sm" onClick={load}>
          <span className="inline-flex items-center gap-2"><RefreshCcw size={14} /> Refresh</span>
        </button>
      </div>

      {error ? <Banner tone="error" message={error} /> : null}
      {success ? <Banner tone="success" message={success} /> : null}

      <section className="superadmin-glass rounded-2xl p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <StyledSelect
            value={filters.type}
            onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
            options={typeOptions}
            placeholder="Filter by type"
          />

          <StyledSelect
            value={filters.project_id}
            onChange={(value) => setFilters((prev) => ({ ...prev, project_id: value }))}
            options={projectOptions}
            searchable
            placeholder="Filter by project"
          />

          <input
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Search user/project/description"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none"
          />

          <button
            type="button"
            className="ems-btn-primary text-sm"
            onClick={() => setFilters({ type: '', project_id: '', search: '' })}
          >
            Clear Filters
          </button>
        </div>
      </section>

      <section className="superadmin-glass rounded-2xl p-4">
        <div className="overflow-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-slate-500">
                <th className="py-2">Date</th>
                <th>User</th>
                <th>Organization</th>
                <th>Project</th>
                <th>Description</th>
                <th>Type</th>
                <th>Amount</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-8 text-center text-slate-500" colSpan={8}>Loading transactions...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500" colSpan={8}>No transactions matched current filters.</td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-50 align-top hover:bg-slate-50/70">
                  <td className="py-3">{row.date || '-'}</td>
                  <td>
                    <p>{row.user_name || '-'}</p>
                    <p className="text-xs text-slate-500">{row.user_email || '-'}</p>
                  </td>
                  <td>{row.organization_name || 'Unassigned'}</td>
                  <td>{row.project_name || '-'}</td>
                  <td>{row.description || '-'}</td>
                  <td><Chip label={row.type || '-'} tone={row.type === 'receipt' ? 'success' : 'edit'} /></td>
                  <td>{(row.currency || 'PKR')} {Number(row.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td>
                    <div className="inline-flex w-full justify-end gap-2">
                      <ActionIconButton icon={Eye} label="View" tone="view" onClick={() => setViewRow(row)} />
                      <ActionIconButton icon={Pencil} label="Edit" tone="edit" onClick={() => openEdit(row)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <SuperAdminPanel
        open={!!viewRow}
        onClose={() => setViewRow(null)}
        title={viewRow ? `Transaction #${viewRow.id}` : ''}
        subtitle={viewRow ? `${viewRow.project_name || 'No project'} · ${viewRow.organization_name || 'Unassigned org'}` : ''}
      >
        {viewRow ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Info label="Date" value={viewRow.date || '-'} />
            <Info label="Type" value={viewRow.type || '-'} />
            <Info label="Amount" value={`${viewRow.currency || 'PKR'} ${Number(viewRow.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} />
            <Info label="Project" value={viewRow.project_name || '-'} />
            <Info label="Organization" value={viewRow.organization_name || '-'} />
            <Info label="User" value={`${viewRow.user_name || '-'} (${viewRow.user_email || '-'})`} />
            <Info label="Description" value={viewRow.description || '-'} />
          </div>
        ) : null}
      </SuperAdminPanel>

      <SuperAdminPanel
        open={!!editRow}
        onClose={() => setEditRow(null)}
        title={editRow ? `Edit Transaction #${editRow.id}` : ''}
        subtitle={editRow ? `${editRow.project_name || 'No project'} · ${editRow.organization_name || 'Unassigned org'}` : ''}
        footer={(
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className="ems-btn-secondary" onClick={() => setEditRow(null)} disabled={saving}>Cancel</button>
            <button type="button" className="ems-btn-primary" onClick={saveTransaction} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        )}
      >
        {editRow ? (
          <div className="space-y-4">
            <FormInput label="Description" value={form.description} onChange={(value) => setForm((prev) => ({ ...prev, description: value }))} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormSelect
                label="Type"
                value={form.type}
                onChange={(value) => setForm((prev) => ({ ...prev, type: value }))}
                options={['receipt', 'payment']}
              />
              <FormInput
                label="Amount"
                type="number"
                value={form.amount}
                onChange={(value) => setForm((prev) => ({ ...prev, amount: value }))}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormInput label="Date" type="date" value={form.date} onChange={(value) => setForm((prev) => ({ ...prev, date: value }))} />
              <FormInput label="Time" type="time" value={form.transaction_time} onChange={(value) => setForm((prev) => ({ ...prev, transaction_time: value }))} />
            </div>
            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Project</span>
              <StyledSelect
                value={form.project_id}
                onChange={(value) => setForm((prev) => ({ ...prev, project_id: value }))}
                options={projectOptions.filter((item) => item.value !== '')}
                searchable
                placeholder="Select project"
              />
            </label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormSelect
                label="Currency"
                value={form.currency}
                onChange={(value) => setForm((prev) => ({ ...prev, currency: value }))}
                options={currencyOptions}
              />
              <FormInput label="Payment Method" value={form.payment_method} onChange={(value) => setForm((prev) => ({ ...prev, payment_method: value }))} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormInput label="Person" value={form.person} onChange={(value) => setForm((prev) => ({ ...prev, person: value }))} />
              <FormInput label="Reference No" value={form.reference_no} onChange={(value) => setForm((prev) => ({ ...prev, reference_no: value }))} />
            </div>
          </div>
        ) : null}
      </SuperAdminPanel>
    </div>
  );
}

function FormInput({ label, value, onChange, type = 'text' }) {
  return (
    <label>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type={type}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none"
      />
    </label>
  );
}

function FormSelect({ label, value, onChange, options }) {
  return (
    <label>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <StyledSelect
        value={value}
        onChange={onChange}
        options={(options || []).map((item) => ({ value: item, label: item }))}
      />
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-700">{value}</p>
    </div>
  );
}

function Banner({ tone, message }) {
  const style = tone === 'error'
    ? { background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }
    : { background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' };

  return (
    <div className="rounded-2xl border px-4 py-3 text-sm font-medium" style={style}>
      {message}
    </div>
  );
}
